import type { ComponentType } from 'react';
import {
  Archive,
  BadgeCheck,
  Check,
  ChevronDown,
  ChevronUp,
  CircleDashed,
  CircleSlash,
  Equal,
  Flame,
  Gavel,
  Hand,
  MessageCircleQuestion,
  OctagonAlert,
  Play,
  Undo2,
} from 'lucide-react';
import {
  PRIMARY_PIPELINE,
  TASK_PRIORITY_META,
  TASK_STATUS_META,
  type TaskPriority,
  type TaskStatus,
} from '@ssn/shared';
import { Badge, type Tone } from '@/components/ui/Badge';
import { cn } from '@/components/ui/cn';

type IconComponent = ComponentType<{ className?: string; 'aria-hidden'?: boolean }>;

/**
 * The icon named by each status in the shared workflow definition.
 *
 * §14 is explicit: status must never be communicated by colour alone. Every
 * status indicator in this application therefore carries three signals — an
 * icon, a text label and a tone — so it survives colour blindness, a monochrome
 * screenshot and a screen reader alike.
 */
const ICONS: Record<string, IconComponent> = {
  'circle-dashed': CircleDashed,
  hand: Hand,
  play: Play,
  'octagon-alert': OctagonAlert,
  'message-circle-question': MessageCircleQuestion,
  check: Check,
  gavel: Gavel,
  'undo-2': Undo2,
  'badge-check': BadgeCheck,
  'circle-slash': CircleSlash,
  archive: Archive,
  'chevron-down': ChevronDown,
  equal: Equal,
  'chevron-up': ChevronUp,
  flame: Flame,
};

function iconFor(name: string): IconComponent {
  return ICONS[name] ?? CircleDashed;
}

export function StatusIcon({ status, className }: { status: TaskStatus; className?: string }) {
  const Icon = iconFor(TASK_STATUS_META[status].icon);
  return <Icon aria-hidden className={cn('size-3.5', className)} />;
}

export function StatusBadge({
  status,
  size = 'md',
  className,
}: {
  status: TaskStatus;
  size?: 'sm' | 'md';
  className?: string;
}) {
  const meta = TASK_STATUS_META[status];
  return (
    <Badge
      tone={meta.tone as Tone}
      size={size}
      title={meta.description}
      icon={<StatusIcon status={status} className={size === 'sm' ? 'size-3' : 'size-3.5'} />}
      className={className}
    >
      {meta.label}
    </Badge>
  );
}

export function PriorityBadge({
  priority,
  size = 'md',
  className,
}: {
  priority: TaskPriority;
  size?: 'sm' | 'md';
  className?: string;
}) {
  const meta = TASK_PRIORITY_META[priority];
  const Icon = iconFor(meta.icon);
  return (
    <Badge
      tone={meta.tone as Tone}
      size={size}
      title={meta.description}
      icon={<Icon aria-hidden className={size === 'sm' ? 'size-3' : 'size-3.5'} />}
      className={className}
    >
      {meta.label}
    </Badge>
  );
}

/**
 * The status pipeline shown at the top of a task (§23).
 *
 * It answers "what stage is this at, and what happens next" at a glance. A task
 * that has left the happy path — changes requested, cancelled — shows its actual
 * state as a trailing step rather than being forced into the linear pipeline,
 * because pretending otherwise would misrepresent where the work stands.
 */
export function WorkflowPipeline({ status, className }: { status: TaskStatus; className?: string }) {
  const onPipeline = PRIMARY_PIPELINE.includes(status);
  const currentIndex = onPipeline ? PRIMARY_PIPELINE.indexOf(status) : -1;
  const steps = onPipeline ? PRIMARY_PIPELINE : [...PRIMARY_PIPELINE.slice(0, 4), status];
  const activeIndex = onPipeline ? currentIndex : steps.length - 1;

  return (
    <ol className={cn('flex flex-wrap items-center gap-x-1 gap-y-2', className)}>
      {steps.map((step, index) => {
        const meta = TASK_STATUS_META[step];
        const isCurrent = index === activeIndex;
        const isDone = index < activeIndex;

        return (
          <li key={step} className="flex items-center gap-1">
            <span
              aria-current={isCurrent ? 'step' : undefined}
              className={cn(
                'inline-flex items-center gap-1.5 rounded-md border px-2 py-1 text-xs font-medium whitespace-nowrap',
                isCurrent && `border-transparent ring-1 ${toneRing(meta.tone)}`,
                isCurrent && toneFilled(meta.tone),
                isDone && 'border-border bg-surface-sunken text-text-muted',
                !isCurrent && !isDone && 'border-dashed border-border bg-transparent text-text-subtle',
              )}
            >
              <StatusIcon status={step} className="size-3" />
              {meta.shortLabel}
              {isCurrent && <span className="sr-only">(current stage)</span>}
            </span>
            {index < steps.length - 1 && (
              <span aria-hidden className="text-text-subtle select-none">
                →
              </span>
            )}
          </li>
        );
      })}
    </ol>
  );
}

function toneFilled(tone: string): string {
  const map: Record<string, string> = {
    neutral: 'bg-neutral-soft text-neutral-tone',
    info: 'bg-info-soft text-info-tone',
    active: 'bg-active-soft text-active-tone',
    warning: 'bg-warning-soft text-warning-tone',
    attention: 'bg-attention-soft text-attention-tone',
    positive: 'bg-positive-soft text-positive-tone',
    success: 'bg-success-soft text-success-tone',
    muted: 'bg-muted-soft text-muted-tone',
  };
  return map[tone] ?? map['neutral']!;
}

function toneRing(tone: string): string {
  const map: Record<string, string> = {
    neutral: 'ring-neutral-border',
    info: 'ring-info-border',
    active: 'ring-active-border',
    warning: 'ring-warning-border',
    attention: 'ring-attention-border',
    positive: 'ring-positive-border',
    success: 'ring-success-border',
    muted: 'ring-muted-border',
  };
  return map[tone] ?? map['neutral']!;
}
